// Resources (Phase 2 preview; FW-006 makes it a grid with filters, after the content grid in the
// inspirations): plans, the library read beside Scripture, practices, and the confessions. A filter
// column at the left (a row of chips on a phone) shows one kind or all; each kind is a grid of cards,
// each card one press with its ↗ disc; the library's companions still stand on a shelf (FS-390). The
// library is the app's own (public/library, scripts/build-books.mjs): works whose text the web may
// show open in the reader; the rest are in the app.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Badge, Chip } from "../../components/Badge";
import { BookCover } from "../../components/BookCover";
import { Icon } from "../../components/Icon";
import { bookWidth, Ledge, objectKind, ShelfBook, ShelfSlot, shelfStyles as sh } from "../../components/library/Shelf";
import { PlanCover } from "../../components/PlanCover";
import { SectionHeader } from "../../components/SectionHeader";
import ui from "../../components/ui.module.css";
import { useJourneys, usePlans } from "../../data/planQueries";
import { toneOf, useAuthors, useCatalogue } from "../../data/works";
import { morphFrom } from "../../motion/shared";
import { useTitle } from "../../lib/useTitle";
import s from "./resources.module.css";

export const Route = createFileRoute("/_app/resources")({ component: Resources });

/** Books of the library to read beside Scripture: the Fathers and the Reformed confessions, all on the web. */
const SHELF = ["ante-nicene-fathers-vol-1", "ante-nicene-fathers-vol-2", "ante-nicene-fathers-vol-3", "beddome-exposition", "1644-london-baptist-confession", "westminster-larger-catechism"];
const CONFESSIONS = ["westminster-confession-of-faith", "1689-london-baptist-confession", "westminster-shorter-catechism", "baptist-catechism"];
const STAND = 156;

const PRACTICES = [
  { name: "Reflect on a passage", text: "Four gentle questions, one at a time, kept for you alone.", to: "/reflect" as const, icon: "note" as const },
  { name: "Pray a psalm", text: "Read a psalm slowly and pray it back in your own words.", to: "/reflect" as const, ref: "Psalm 46:1", icon: "praying" as const },
  { name: "Learn a verse by heart", text: "A verse a week, said over until it stays.", to: null, icon: "bookmark" as const },
  { name: "Copy by hand", text: "Write a passage out slowly; notice what your hand slows on.", to: null, icon: "edit" as const },
];

type Show = "all" | "plans" | "library" | "practices" | "confessions";

function Resources() {
  useTitle("Resources");
  const { data: plans } = usePlans();
  const { data: journeys = [] } = useJourneys();
  const { data: catalogue } = useCatalogue();
  const { data: authors } = useAuthors();
  const [show, setShow] = useState<Show>("all");
  const works = (ids: string[]) => ids.map((id) => catalogue?.works.find((w) => w.id === id)).filter((w) => !!w);
  const shelf = works(SHELF);
  const confessions = works(CONFESSIONS);
  const kinds: { id: Show; label: string; n?: number }[] = [
    { id: "all", label: "Everything" },
    { id: "plans", label: "Reading plans", n: plans?.length },
    { id: "library", label: "With the library", n: shelf.length },
    { id: "practices", label: "Practices", n: PRACTICES.length },
    { id: "confessions", label: "Confessions", n: confessions.length },
  ];
  const on = (k: Show) => show === "all" || show === k;

  return (
    <div className={ui.pageWide}>
      <header className={s.head}>
        <h1 className={ui.title}>Resources</h1>
        <p className={ui.subtitle}>Ways into Scripture that the church has used for centuries, with the library beside them.</p>
      </header>

      <div className={s.layout}>
        <div className={s.filters} role="radiogroup" aria-label="Show">
          <p className={s.filtersHead} aria-hidden="true">
            Show
          </p>
          {kinds.map((k) => (
            <Chip key={k.id} role="radio" aria-checked={show === k.id} className={s.filter} onClick={() => setShow(k.id)}>
              <span>{k.label}</span>
              {k.n !== undefined && <span className={s.filterN}>{k.n}</span>}
            </Chip>
          ))}
        </div>

        <div className={s.results} key={show}>
          {on("plans") && (
            <section className={s.section} aria-labelledby="plans-head">
              <SectionHeader id="plans-head" title="Reading plans" />
              <ul className={s.grid}>
                {plans?.map((p) => {
                  const mine = journeys.find((j) => j.plan.id === p.id);
                  const inner = (
                    <>
                      <span className={s.cardTop}>
                        <PlanCover plan={p} width={56} />
                        {mine && <Badge tone="solid">Reading</Badge>}
                        <span className={`${ui.goDisc} ${s.cardGo}`} aria-hidden="true">
                          <Icon name="go" size={16} />
                        </span>
                      </span>
                      <span className={s.cardName}>{p.title}</span>
                      {p.description && <span className={s.cardText}>{p.description}</span>}
                      <span className={s.cardMeta}>{p.companionWork?.title ? `${p.days} days, with ${p.companionWork.title}` : `${p.days} days, alone or with friends`}</span>
                    </>
                  );
                  return (
                    <li key={p.id}>
                      {mine ? (
                        <Link to="/plans/$journeyId" params={{ journeyId: mine.id }} className={s.card}>
                          {inner}
                        </Link>
                      ) : (
                        <Link to="/plans/about/$planId" params={{ planId: p.id }} className={s.card}>
                          {inner}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {on("library") && (
            <section className={s.section} aria-labelledby="pair-head">
              <SectionHeader
                id="pair-head"
                title="Read with the library"
                action={
                  <Link to="/authors" className={s.more}>
                    All the authors
                  </Link>
                }
              />
              <Ledge height={STAND} labelledBy="pair-head">
                {shelf.map((w) => (
                  <li key={w.id}>
                    <Link to="/books/$id" params={{ id: w.id }} className={sh.slot} onClick={morphFrom("book", w.id)}>
                      <ShelfSlot
                        width={bookWidth(STAND, objectKind(w.kind))}
                        book={<ShelfBook id={w.id} title={w.title} shortTitle={w.shortTitle} author={w.author} tone={toneOf(w, authors)} kind={w.kind} height={STAND} />}
                        caption={
                          <>
                            <span className={sh.title}>{w.title}</span>
                            <span className={sh.meta}>{[w.author, w.written].filter(Boolean).join(", ")}</span>
                          </>
                        }
                      />
                    </Link>
                  </li>
                ))}
              </Ledge>
            </section>
          )}

          {on("practices") && (
            <section className={s.section} aria-labelledby="practice-head">
              <SectionHeader id="practice-head" title="Practices" />
              <ul className={s.grid}>
                {PRACTICES.map((x) => {
                  const inner = (
                    <>
                      <span className={s.cardTop}>
                        <span className={s.glyph} aria-hidden="true">
                          <Icon name={x.icon} size={20} />
                        </span>
                        {x.to && (
                          <span className={`${ui.goDisc} ${s.cardGo}`} aria-hidden="true">
                            <Icon name="go" size={16} />
                          </span>
                        )}
                      </span>
                      <span className={s.cardName}>{x.name}</span>
                      <span className={s.cardText}>{x.text}</span>
                    </>
                  );
                  return (
                    <li key={x.name}>
                      {x.to ? (
                        <Link to={x.to} search={x.ref ? { ref: x.ref } : {}} className={s.card}>
                          {inner}
                        </Link>
                      ) : (
                        <div className={`${s.card} ${s.still}`}>{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {on("confessions") && (
            <section className={s.section} aria-labelledby="conf-head">
              <SectionHeader id="conf-head" title="Confessions and catechisms" />
              <ul className={s.grid}>
                {confessions.map((w) => (
                  <li key={w.id}>
                    <Link to="/books/$id" params={{ id: w.id }} className={`${s.card} ${s.bookCard}`} onClick={morphFrom("book", w.id)}>
                      <span data-morph="book" className={s.bookCover}>
                        <BookCover id={w.id} title={w.title} shortTitle={w.shortTitle} author={w.author} tone={toneOf(w, authors)} width={64} decorative />
                      </span>
                      <span className={s.bookWords}>
                        <span className={s.tag}>{/catechism/i.test(w.title) ? "Catechism" : "Confession"}</span>
                        <span className={s.cardName}>{w.title}</span>
                        {w.written && <span className={s.cardMeta}>{w.written}</span>}
                      </span>
                      <span className={`${ui.goDisc} ${s.cardGo}`} aria-hidden="true">
                        <Icon name="go" size={16} />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
